/**
 * Liquid Custom Filters
 *
 * This file contains custom Liquid filters for template rendering.
 */

import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';

/**
 * Register custom filters to a Liquid engine instance
 *
 * @param {Liquid} engine - Liquid engine instance
 */
export function registerCustomFilters(engine) {
  /**
   * Barcode filter - Generate barcode SVG
   *
   * Usage in Liquid template:
   *   {{ fulfillment.number | barcode }}
   *   {{ fulfillment.number | barcode: 'code128' }}
   *   {{ fulfillment.number | barcode: 'code128', 50 }}  (with custom height)
   *
   * @param {string} value - Barcode value
   * @param {string} format - Barcode format (default: 'CODE128')
   * @param {number} height - Barcode height in pixels (default: 50)
   * @returns {string} SVG HTML string wrapped in a responsive container
   */
  engine.registerFilter('barcode', (value, format = 'CODE128', height = 50) => {
    if (!value) {
      return '';
    }

    try {
      // Create a temporary SVG element
      const svgNS = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('xmlns', svgNS);

      // Generate barcode
      JsBarcode(svg, String(value), {
        format: format.toUpperCase(),
        height,
        displayValue: true,
        fontSize: 14,
        margin: 5,
        background: 'transparent',
        width: 2  // Bar width (narrower bars for better fit)
      });

      // Get SVG outerHTML and fix viewBox if needed
      let svgHtml = svg.outerHTML;

      // If viewBox has units, remove them
      svgHtml = svgHtml.replace(/viewBox="([0-9.]+)px\s+([0-9.]+)px\s+([0-9.]+)px\s+([0-9.]+)px"/g,
                                'viewBox="$1 $2 $3 $4"');

      // Remove width/height units for better scaling
      svgHtml = svgHtml.replace(/width="([0-9.]+)px"/g, 'width="$1"');
      svgHtml = svgHtml.replace(/height="([0-9.]+)px"/g, 'height="$1"');

      // Wrap SVG in a responsive container with max-width constraint
      return `
        <div style="
          display: inline-block;
          max-width: 100%;
          text-align: center;
        ">
          ${svgHtml}
        </div>
      `;
    } catch (error) {
      console.error('Barcode generation error:', error);
      return `<div style="color: red;">Invalid barcode: ${value}</div>`;
    }
  });

  /**
   * QR Code filter - Generate QR code SVG
   *
   * Usage in Liquid template:
   *   {{ fulfillment.number | qrcode }}
   *   {{ fulfillment.number | qrcode: 200 }}
   *   {{ fulfillment.number | qrcode: 200, 'H' }}
   *
   * @param {string} value - QR code value
   * @param {number} size - QR code size in pixels (default: 200)
   * @param {string} errorCorrectionLevel - Error correction level: 'L', 'M', 'Q', 'H' (default: 'M')
   * @returns {string} SVG HTML string
   */
  engine.registerFilter('qrcode', (value, size = 200, errorCorrectionLevel = 'M') => {
    if (!value) {
      return '';
    }

    try {
      // Validate error correction level
      const validLevels = ['L', 'M', 'Q', 'H'];
      const level = validLevels.includes(String(errorCorrectionLevel).toUpperCase())
        ? String(errorCorrectionLevel).toUpperCase()
        : 'M';

      // Generate QR code as SVG
      let svgString = '';
      QRCode.toString(String(value), {
        type: 'svg',
        width: size,
        errorCorrectionLevel: level,
        margin: 1
      }, (err, svg) => {
        if (err) {
          throw err;
        }
        svgString = svg;
      });

      // Return wrapped SVG
      return `
        <div style="display: inline-block; text-align: center; margin: 10px 0;">
          ${svgString}
          <div style="font-family: monospace; font-size: 12px; margin-top: 5px; word-break: break-all; max-width: ${size}px;">${value}</div>
        </div>
      `;
    } catch (error) {
      console.error('QR code generation error:', error);
      return `<div style="color: red;">Invalid QR code: ${value}</div>`;
    }
  });
}
